/**
 * Display-only masking for the vendor wizard request preview (fe-admin-portal#121).
 *
 * The Connect step hides credentials, but the "Live Test Request JSON" panel and the
 * "Confirm Request JSON" dialog showed the request as sent, e.g.
 * `Authorization: Bearer <full key>`. These helpers return a masked COPY for the screen.
 * The live-test request itself is built from the unmasked values and never passes through here.
 */

export const SECRET_PREVIEW_MASK = '********';

/** Words that mark a header, query param or body field as a credential. */
const SECRET_NAME_WORDS = new Set([
  'key',
  'apikey',
  'token',
  'secret',
  'password',
  'passwd',
  'pwd',
  'authorization',
  'auth',
  'credential',
  'credentials',
  'signature',
  'cookie',
  'bearer',
]);

/** Joined forms (`accessToken`, `client_secret`, `X-Api-Key`) after removing separators. */
const SECRET_NAME_FRAGMENTS = [
  'apikey',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'authtoken',
  'sessiontoken',
  'clientsecret',
  'privatekey',
  'secretkey',
];

/** Connect-step auth fields that hold a secret value (not an env name, URL or header name). */
const AUTH_CONFIG_SECRET_FIELDS = [
  'apiKey',
  'key',
  'token',
  'password',
  'clientSecret',
  'accountsApiKey',
  'flinksApiKey',
  'tokenAuthKey',
];

/** Shorter values are not treated as secrets: replacing them would mangle unrelated text. */
const MIN_SECRET_LENGTH = 4;

const AUTH_SCHEME_RE = /^(Bearer|Basic|Token|Digest|Negotiate)\s+(.*)$/is;
const PLACEHOLDER_RE = /^\{\{[^{}]+\}\}$/;

export interface RequestPreview {
  url: string;
  method: string;
  headers: Record<string, string>;
  bodyType?: string;
  body?: unknown;
}

function nameWords(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** True when a header, query param or body field name says it carries a credential. */
export function isSecretFieldName(name: string): boolean {
  if (nameWords(name).some(word => SECRET_NAME_WORDS.has(word))) return true;
  const compact = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return SECRET_NAME_FRAGMENTS.some(fragment => compact.includes(fragment));
}

function isPlaceholder(value: string): boolean {
  return PLACEHOLDER_RE.test(value.trim());
}

/** Masks a credential value; keeps an auth scheme (`Bearer`) and `{{...}}` templates readable. */
export function maskSecretValue(value: string): string {
  if (!value || isPlaceholder(value)) return value;
  const scheme = AUTH_SCHEME_RE.exec(value);
  if (scheme) {
    const rest = scheme[2].trim();
    return `${scheme[1]} ${rest && isPlaceholder(rest) ? rest : SECRET_PREVIEW_MASK}`;
  }
  return SECRET_PREVIEW_MASK;
}

/**
 * Secret values the operator entered on the Connect step (plus provider variables whose name
 * marks them as credentials). Used to mask the same value wherever else it appears.
 */
export function collectPreviewSecretValues(
  authConfig: Record<string, string | undefined>,
  providerVariables: Record<string, string> = {},
  ignoredValues: readonly string[] = [],
): string[] {
  const ignored = new Set(ignoredValues);
  const values = [
    ...AUTH_CONFIG_SECRET_FIELDS.map(field => authConfig[field]),
    ...Object.entries(providerVariables)
      .filter(([name]) => isSecretFieldName(name))
      .map(([, value]) => value),
  ];
  const out = new Set<string>();
  for (const raw of values) {
    const value = raw?.trim() ?? '';
    if (value.length < MIN_SECRET_LENGTH || ignored.has(value) || isPlaceholder(value)) continue;
    out.add(value);
  }
  return Array.from(out);
}

function secretVariants(secrets: readonly string[]): string[] {
  const variants = new Set<string>();
  for (const secret of secrets) {
    if (secret.length < MIN_SECRET_LENGTH) continue;
    variants.add(secret);
    variants.add(encodeURIComponent(secret));
  }
  // Longest first, so a secret that contains another is replaced whole.
  return Array.from(variants).sort((a, b) => b.length - a.length);
}

function replaceKnownSecrets(text: string, variants: readonly string[]): string {
  let out = text;
  for (const secret of variants) {
    if (out.includes(secret)) out = out.split(secret).join(SECRET_PREVIEW_MASK);
  }
  return out;
}

function maskUrl(url: string, variants: readonly string[]): string {
  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const queryIndex = beforeHash.indexOf('?');
  let base = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : null;

  // https://user:password@host/... -> https://********@host/...
  base = base.replace(/^([a-z][a-z0-9+.-]*:\/\/)[^/@?#]+@/i, `$1${SECRET_PREVIEW_MASK}@`);

  const maskedQuery = query === null
    ? null
    : query
      .split('&')
      .map(pair => {
        const eq = pair.indexOf('=');
        if (eq < 0) return pair;
        const rawName = pair.slice(0, eq);
        const rawValue = pair.slice(eq + 1);
        let name = rawName;
        let value = rawValue;
        try {
          name = decodeURIComponent(rawName.replace(/\+/g, ' '));
          value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
        } catch {
          // Keep the raw text when it is not valid percent-encoding.
        }
        return isSecretFieldName(name) && value && !isPlaceholder(value)
          ? `${rawName}=${SECRET_PREVIEW_MASK}`
          : pair;
      })
      .join('&');

  const rebuilt = `${base}${maskedQuery === null ? '' : `?${maskedQuery}`}${hash}`;
  return replaceKnownSecrets(rebuilt, variants);
}

function maskBodyValue(value: unknown, variants: readonly string[], forceMask: boolean): unknown {
  if (Array.isArray(value)) return value.map(item => maskBodyValue(item, variants, forceMask));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        maskBodyValue(item, variants, forceMask || isSecretFieldName(key)),
      ]),
    );
  }
  if (typeof value === 'string') {
    return forceMask ? maskSecretValue(value) : replaceKnownSecrets(value, variants);
  }
  if (forceMask && (typeof value === 'number' || typeof value === 'bigint')) return SECRET_PREVIEW_MASK;
  return value;
}

/**
 * Returns a masked copy of the request preview for display. Masks:
 * - headers whose name marks a credential (Authorization, x-api-key, api-key, ...);
 * - query params whose name marks a credential, and `user:password@` in the URL;
 * - body fields whose name marks a credential (nested values too);
 * - any Connect-step secret value wherever it appears (URL, headers, body).
 * The input is not changed.
 */
export function maskRequestPreviewForDisplay<T extends RequestPreview>(
  preview: T,
  secretValues: readonly string[] = [],
): T {
  const variants = secretVariants(secretValues);
  const headers = Object.fromEntries(
    Object.entries(preview.headers ?? {}).map(([name, value]) => {
      const text = typeof value === 'string' ? value : String(value ?? '');
      return [name, isSecretFieldName(name) ? maskSecretValue(text) : replaceKnownSecrets(text, variants)];
    }),
  );
  const masked: T = {
    ...preview,
    url: maskUrl(preview.url ?? '', variants),
    headers,
  };
  if ('body' in preview && preview.body !== undefined) {
    masked.body = maskBodyValue(preview.body, variants, false);
  }
  return masked;
}

/** Pretty JSON of the masked preview, as shown in the wizard editors. */
export function requestPreviewDisplayText(
  preview: RequestPreview,
  secretValues: readonly string[] = [],
): string {
  return JSON.stringify(maskRequestPreviewForDisplay(preview, secretValues), null, 2);
}
